const BACKEND_URL = process.env.REACT_APP_BACKEND_URL + "/v1";

/**
 * Enhance a job posting using the backend AI endpoint (SSE streaming).
 * @param {string} jobInput - User's raw job description
 * @param {function} onStreamUpdate - Called with the accumulating raw text on each chunk
 * @returns {Promise<object>} - Parsed enhanced job data
 */
export const enhanceJobWithAI = async (jobInput, onStreamUpdate = null) => {
  const token = localStorage.getItem("x-access-token");

  const response = await fetch(`${BACKEND_URL}/ai/enhance-job`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-access-token": token,
    },
    body: JSON.stringify({ jobInput }),
  });

  // Handle non-streaming error responses (e.g. 400, 403)
  if (!response.ok) {
    const errorData = await response.json();
    throw { response: { data: errorData } };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullText = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const rawChunk = decoder.decode(value, { stream: true });

    // SSE lines come as: "data: {...}\n\n"
    const lines = rawChunk.split("\n").filter((l) => l.startsWith("data: "));

    for (const line of lines) {
      const jsonStr = line.replace("data: ", "").trim();
      let parsed;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        continue;
      }

      if (parsed.error) {
        throw new Error(parsed.error);
      }

      if (parsed.done && parsed.result) {
        return parsed.result;
      }

      if (parsed.chunk) {
        fullText += parsed.chunk;
        if (onStreamUpdate) {
          onStreamUpdate(fullText);
        }
      }
    }
  }

  throw new Error("AI enhancement ended without a result.");
};

/**
 * Generate a job image via the backend AI endpoint.
 * Returns a base64 data URL.
 * @param {string} jobDescription
 * @returns {Promise<string>} base64 data URL
 */
export const generateJobImage = async (jobDescription) => {
  const token = localStorage.getItem("x-access-token");

  const response = await fetch(`${BACKEND_URL}/ai/generate-image`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-access-token": token,
    },
    body: JSON.stringify({ jobDescription }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to generate job image.");
  }

  const data = await response.json();
  const base64DataUrl = data.data.image;

  // Compress before returning
  return await compressBase64Image(base64DataUrl, 0.6);
};

// ---------------------------------------------------------------------------
// Helpers — no API key needed, remain on the frontend
// ---------------------------------------------------------------------------

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_MAP_KEY}`
          );
          const data = await response.json();
          if (data.results && data.results[0]) {
            resolve({ latitude, longitude, address: data.results[0].formatted_address });
          } else {
            resolve({ latitude, longitude, address: `${latitude}, ${longitude}` });
          }
        } catch (error) {
          console.error("Error getting address:", error);
          resolve({ latitude, longitude, address: `${latitude}, ${longitude}` });
        }
      },
      (error) => reject(error)
    );
  });
};

const compressBase64Image = (base64DataUrl, quality = 0.6) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 512, 512);
        resolve(canvas.toDataURL("image/jpeg", quality));
      } catch {
        resolve(base64DataUrl);
      }
    };
    img.onerror = () => resolve(base64DataUrl);
    img.src = base64DataUrl;
  });
};

export const base64ToFile = async (base64DataUrl, filename = "ai-generated-job-image.jpg") => {
  const base64Data = base64DataUrl.split(",")[1];
  const mimeType = base64DataUrl.split(",")[0].split(":")[1].split(";")[0];
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
};

export const urlToFile = async (url, filename = "ai-generated-job-image.png") => {
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type || "image/png" });
  } catch (fetchError) {
    console.warn("Direct fetch failed:", fetchError.message);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) resolve(new File([blob], filename, { type: "image/png" }));
          else reject(new Error("Failed to convert canvas to blob"));
        }, "image/png", 0.95);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = url;
      setTimeout(() => reject(new Error("Image load timeout")), 30000);
    });
  }
};
